import { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useAuthStore from '../store/auth-store'
import { ChevronDown, LogOut, Settings, FolderOpen, Shield, ShieldCheck, AlertCircle, Loader, Check } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { CreateProjectModal } from './CreateProjectModal'
import { AccountModal } from './AccountModal'
import { TwoFactorModal } from './TwoFactorModal'

export function UserDropdown() {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { user, logout } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState(null)
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false)
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Get user display name
  const displayName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}` 
    : user?.email?.split('@')[0] || 'User'

  // Check if user has admin/superadmin role
  // Check for: is_superuser flag, or groups containing 'admin', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', etc.
  const hasAdminRole = user?.is_superuser || user?.groups?.some(group => 
    group?.toLowerCase?.().includes('super') || 
    group?.toLowerCase?.().includes('role_super_admin')
  )
  
  const hasAdminAccess = user?.is_staff || user?.groups?.some(group => 
    group?.toLowerCase?.().includes('admin')
  ) || hasAdminRole

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch projects when dropdown opens or on hover
  useEffect(() => {
    if (isOpen && projects.length === 0 && !loadingProjects && !error) {
      fetchUserProjects()
    }
  }, [isOpen])

  const handleButtonHover = () => {
    // Fetch projects on hover if not already loaded
    if (projects.length === 0 && !loadingProjects && !error) {
      fetchUserProjects()
    }
  }

  const fetchUserProjects = async () => {
    setLoadingProjects(true)
    setError(null) // Clear previous errors before fetching
    try {
      const response = await projectsApi.fetchProjects(1, 100)

      // Handle the response - could be the API response object or error
      if (!response) {
        throw new Error('No response from server')
      }

      if (response.success && Array.isArray(response.data)) {
        setProjects(response.data)
        setError(null)
      } else if (Array.isArray(response)) {
        // If response is directly an array (in case API returns array directly)
        setProjects(response)
        setError(null)
      } else {
        throw new Error(response.message || 'Invalid response format')
      }
    } catch (err) {
      // 401 is handled by the global session-expiry interceptor — it will
      // redirect to /login with a friendly flash. Don't render a competing
      // "Retry" error in the dropdown for auth failures; that just confuses
      // the user mid-redirect.
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        setProjects([])
        setError(null)
        return
      }
      console.error('Error fetching projects:', err)
      setProjects([])
      setError('Unable to load projects. Please try again.')
    } finally {
      setLoadingProjects(false)
    }
  }

  const handleRetryProjects = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setProjects([])
    fetchUserProjects()
  }

  const handleProjectClick = (projectId) => {
    navigate(`/dashboard/project/${projectId}`)
    setIsOpen(false)
  }

  const handleCreateProjectSuccess = (newProject) => {
    // Refresh projects list
    setProjects([])
    fetchUserProjects()
    setIsCreateModalOpen(false)
    // Optionally navigate to the new project
    if (newProject?.id) {
      navigate(`/dashboard/project/${newProject.id}`)
      setIsOpen(false)
    }
  }

  const handleLogout = () => {
    logout()
    setIsOpen(false)
    window.location.href = '/'
  }

  const handleSuperAdminAccess = async () => {
    // Verify role before allowing access
    if (!hasAdminAccess) {
      setError('You do not have admin privileges')
      setTimeout(() => setError(null), 3000)
      return
    }
    
    try {
      // Get the JWT token from localStorage
      const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
      const token = localStorage.getItem(JWT_STORAGE_KEY)
      
      if (!token) {
        setError('Authentication token not found. Please login again.')
        setTimeout(() => setError(null), 3000)
        return
      }
      
      // Call the API to generate SSO token
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const response = await fetch(`${API_URL}/api/sso-token/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to generate SSO token')
      }
      
      const data = await response.json()
      
      if (data.success && data.redirect_url) {
        // Redirect to Django SSO validation endpoint
        window.location.href = data.redirect_url
      } else {
        throw new Error(data.error || 'Failed to generate SSO token')
      }
    } catch (err) {
      console.error('[SSO] Error:', err)
      setError(err.message || 'Failed to access admin panel')
      setTimeout(() => setError(null), 3000)
    }
    
    setIsOpen(false)
  }

  if (!user) return null

  return (
    <div className="relative" ref={dropdownRef} onMouseLeave={() => setIsOpen(false)}>
      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => {
          setIsOpen(true)
          handleButtonHover()
        }}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition"
      >
        <span className="font-medium text-sm">Welcome {displayName}</span>
        <ChevronDown 
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-6 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900">{displayName}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-200">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {error}
                </p>
                <button
                  onClick={handleRetryProjects}
                  className="text-xs font-semibold text-red-600 hover:text-red-800 whitespace-nowrap"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div className="py-1">
            {/* Account */}
            <button
              onClick={() => {
                setIsAccountModalOpen(true)
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
            >
              <Settings className="w-4 h-4" />
              <span>Account</span>
            </button>

            {/* Two-Factor Authentication */}
            <button
              onClick={() => {
                setIsTwoFactorModalOpen(true)
                setIsOpen(false)
              }}
              className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
            >
              <ShieldCheck className="w-4 h-4 text-[#358ECD] flex-shrink-0" />
              <span>Two-Factor Authentication</span>
            </button>

            {/* Assigned Projects Section */}
            {projects.length > 0 && (
              <>
                <div className="border-t border-gray-200 mt-1"></div>
                
                {projects.map((project) => {
                  const isSelected = projectId && project.id.toString() === projectId.toString()
                  return (
                    <button
                      key={project.id}
                      onClick={() => handleProjectClick(project.id)}
                      className={`w-full text-left px-4 py-2 text-sm transition flex items-center gap-2 ${
                        isSelected
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <FolderOpen className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate flex-1">{project.name}</span>
                      {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
                    </button>
                  )
                })}
              </>
            )}

            {/* Loading Projects State */}
            {loadingProjects && !error && (
              <div className="px-4 py-3 flex items-center justify-center gap-2 border-t border-gray-200">
                <Loader className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-xs text-gray-600">Loading projects...</span>
              </div>
            )}

            {/* Add Project / Projects Button */}
            <div className="border-t border-gray-200">
              <button
                onClick={() => {
                  setIsCreateModalOpen(true)
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition flex items-center gap-2"
              >
                <span>+ Add Project</span>
              </button>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 my-1"></div>

            {/* Admin/Superadmin - Conditional */}
            {hasAdminRole && (
              <button
                onClick={handleSuperAdminAccess}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <Shield className="w-4 h-4 text-orange-600" />
                <span>SUPER ADMIN</span>
              </button>
            )}
            {hasAdminAccess && !hasAdminRole && (
              <button
                onClick={handleSuperAdminAccess}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <Shield className="w-4 h-4 text-blue-600" />
                <span>ADMIN</span>
              </button>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
            >
              <LogOut className="w-4 h-4" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal 
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateProjectSuccess}
      />

      {/* Account Modal */}
      <AccountModal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
      />

      {/* Two-Factor Authentication Modal */}
      <TwoFactorModal
        isOpen={isTwoFactorModalOpen}
        onClose={() => setIsTwoFactorModalOpen(false)}
      />
    </div>
  )
}
